import { useEffect, useState } from 'react'
import { getRouteApi, Link } from '@tanstack/react-router'
import { MapPin, Search, Star, Users } from 'lucide-react'

import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/field'
import { Skeleton } from '@/components/ui/skeleton'
import { useExperts } from '@/features/marketplace/api'
import { formatNumber, formatRupiah } from '@/lib/format'

const route = getRouteApi('/public/talenta')

const availabilityTone = {
  open: { label: 'Menerima proyek', tone: 'success' as const },
  limited: { label: 'Terbatas', tone: 'warning' as const },
  closed: { label: 'Tidak menerima', tone: 'neutral' as const },
}

export function TalentPage() {
  const search = route.useSearch()
  const navigate = route.useNavigate()
  const [draft, setDraft] = useState(search.q ?? '')

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if ((search.q ?? '') === draft) return
      navigate({ search: (previous) => ({ ...previous, q: draft || undefined, page: 1 }), replace: true })
    }, 350)
    return () => window.clearTimeout(timer)
  }, [draft, navigate, search.q])

  const { data, isLoading } = useExperts({ q: search.q, skill: search.skill, page: search.page })
  const experts = data?.experts ?? []

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6">
      <header className="max-w-2xl">
        <h1 className="text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">Direktori talenta</h1>
        <p className="mt-2 text-muted">
          Praktisi yang mengajar di Skava dan siap mengerjakan proyek Anda dengan jaminan escrow.
        </p>
      </header>

      <div className="relative mt-8 max-w-xl">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-subtle" />
        <Input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Cari nama atau keahlian"
          aria-label="Cari talenta"
          className="pl-10"
        />
      </div>

      {isLoading ? (
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-56 rounded-2xl" />
          ))}
        </div>
      ) : experts.length === 0 ? (
        <Card className="mt-8">
          <EmptyState
            icon={<Users className="size-6" />}
            title="Belum ada talenta yang cocok"
            description="Coba kata kunci lain, atau posting kebutuhan Anda di papan proyek agar talenta yang menghubungi Anda."
            action={
              <Link to="/proyek">
                <Button variant="outline">Buka papan proyek</Button>
              </Link>
            }
          />
        </Card>
      ) : (
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {experts.map((expert) => {
            const availability = availabilityTone[expert.availability]

            return (
              <Card key={expert.user_id} className="flex flex-col">
                <CardBody className="flex-1">
                  <div className="flex items-start gap-3">
                    <Avatar name={expert.full_name} src={expert.avatar_url || undefined} size="lg" />
                    <div className="min-w-0">
                      <p className="truncate font-bold text-ink">{expert.full_name}</p>
                      <p className="clamp-2 mt-0.5 text-sm text-muted">{expert.headline || 'Tenaga ahli Skava'}</p>
                      {expert.city ? (
                        <p className="mt-1 inline-flex items-center gap-1 text-xs text-subtle">
                          <MapPin className="size-3.5" />
                          {expert.city}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-1.5">
                    <Badge tone={availability.tone}>{availability.label}</Badge>
                    {expert.skills.slice(0, 3).map((skill) => (
                      <Badge key={skill}>{skill}</Badge>
                    ))}
                  </div>

                  <div className="mt-4 flex items-center gap-4 text-sm text-muted">
                    {expert.rating_count > 0 ? (
                      <span className="inline-flex items-center gap-1">
                        <Star className="size-3.5 fill-amber-400 text-amber-400" />
                        {expert.rating.toFixed(1)}
                      </span>
                    ) : null}
                    <span>{formatNumber(expert.projects_done)} proyek selesai</span>
                  </div>
                </CardBody>

                <div className="flex items-center justify-between border-t border-line p-4">
                  <p className="text-sm font-semibold text-ink">
                    {expert.hourly_rate ? `${formatRupiah(expert.hourly_rate)}/jam` : 'Tarif menyesuaikan'}
                  </p>
                  <Link to="/talenta/$expertId" params={{ expertId: expert.user_id }}>
                    <Button size="sm" variant="outline">
                      Lihat profil
                    </Button>
                  </Link>
                </div>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
